import { PremiumSessionAggregate } from 'types/premiumSession';
import { useMemo } from 'react';
import { extractFromSlug } from 'helpers/utils';
import useMobxStores from './useMobxStores';

function getPremiumSessionBySlug(
  premiumSessionAggregateList: PremiumSessionAggregate[] | undefined,
  slug: string,
  code: string,
) {
  let premiumSessionAggregate: PremiumSessionAggregate | undefined;
  let hasPremiumSessions = false;
  let hasPremiumSessionsInAnyVersion = false;
  const slugsWithPremiumSessions: string[] = [];

  premiumSessionAggregateList?.forEach((sa) => {
    const { code: saCode } = extractFromSlug(sa.content.offering_slug);
    if (saCode === code) {
      const saHasPremiumSessions = sa.content?.sessions?.length > 0;

      if (sa.content.offering_slug === slug) {
        premiumSessionAggregate = sa;
        hasPremiumSessions = hasPremiumSessions || saHasPremiumSessions;
      }

      hasPremiumSessionsInAnyVersion =
        hasPremiumSessionsInAnyVersion || saHasPremiumSessions;

      if (saHasPremiumSessions) {
        slugsWithPremiumSessions.push(sa.content.offering_slug);
      }
    }
  });

  return {
    premiumSessionAggregate,
    hasPremiumSessions,
    hasPremiumSessionsInAnyVersion,
    slugsWithPremiumSessions,
  };
}

type UseCoursePremiumSessionReturn = {
  premiumSessionAggregate: PremiumSessionAggregate | undefined;
  sessionCount: number;
  /**
   * True if premium session list (content) is available.
   */
  hasPremiumSessions: boolean;
  /**
   * True if there are premium sessions in any version of the course.
   */
  hasPremiumSessionsInAnyVersion: boolean;
  /**
   * List of course versions (offering slugs) that have premium sessions.
   */
  slugsWithPremiumSessions?: string[];
};

/**
 * Returns the PremiumSessionAggregate for the given course, if it exists.
 * This hook is used to access the course premium session data from the MobX stores.
 * @returns
 */
export default function useCoursePremiumSession({
  slug,
}: {
  slug: string;
}): UseCoursePremiumSessionReturn {
  const { premiumSessionsStore } = useMobxStores();
  const { premiumSessionAggregateList } = premiumSessionsStore;
  const { code } = extractFromSlug(slug);

  const {
    premiumSessionAggregate,
    hasPremiumSessions,
    hasPremiumSessionsInAnyVersion,
    slugsWithPremiumSessions,
  } = useMemo(
    () => getPremiumSessionBySlug(premiumSessionAggregateList, slug, code),
    [premiumSessionAggregateList, slug, code],
  );

  const sessionCount = premiumSessionAggregate?.content?.sessions?.length || 0;

  return {
    premiumSessionAggregate,
    hasPremiumSessions,
    hasPremiumSessionsInAnyVersion,
    slugsWithPremiumSessions,
    sessionCount,
  };
}
